/**
 * Optional GA4 analytics adapter for Mirrably widgets.
 *
 * Consumers who have already loaded the gtag snippet on their page can call
 * `createGA4Callback(measurementId)` and pass the result to
 * `widget.analytics.addCallback()`.
 *
 * The adapter is intentionally thin -- it delegates completely to the existing
 * gtag call on the page and never loads any external scripts itself.
 *
 * @example
 * ```ts
 * import { createGA4Callback } from '@mirrably/core/ga4';
 *
 * const widget = document.querySelector('forge-tryon');
 * widget.analytics.addCallback(createGA4Callback('G-XXXXXXXXXX'));
 * ```
 */

import type { AnalyticsCallback } from './analytics.js';

type GA4Command = (...args: unknown[]) => void;
type WindowWithGA4 = Window & { gtag?: GA4Command };

/**
 * Create an AnalyticsCallback that forwards events to Google Analytics 4 via
 * the `gtag()` function already present on the page.
 *
 * If `gtag` is not available at the time an event fires, the call is silently
 * skipped -- the consumer is responsible for loading the GA4 snippet.
 *
 * @param measurementId - GA4 measurement ID (e.g. "G-XXXXXXXXXX"). Currently
 *   unused at call time because gtag routes by the snippet config, but kept
 *   here for documentation / future use when multiple GA properties coexist.
 */
export function createGA4Callback(_measurementId: string): AnalyticsCallback {
  return (event) => {
    if (typeof window !== 'undefined') {
      const windowWithGA4 = window as WindowWithGA4;
      if (typeof windowWithGA4.gtag === 'function') {
        windowWithGA4.gtag('event', event.name, event.properties);
      }
    }
  };
}
